import { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import styles from "./PublicRegistration.module.css";
import { ClientModel, ProductsModel, WorkPointModel } from "../../Utils/Models";
import { OrderPayload } from "../../Utils/Types";
import { getData } from "../../Services/getData";
import { endpoints } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import { Vocabulary } from "../../Utils/Vocabulary";
import BenefitsSection from "./BenefitsSection";
import ClientFormStep from "./ClientFormStep";
import * as yup from "yup";
import { EContactStatus } from "../../Utils/Enums";
import appendFormData, {
  getOrderAdvanceAmount,
  isValidDate,
} from "../../Utils/Utils";
import { OrderSource, PAYMENT_TYPES } from "../../Utils/Constants";
import { generatePaymentUrl } from "../../Utils/PaymentUtils";
import {
  loadTikTokPixel,
  sendLeadEventTikTok,
} from "../../Utils/LoadTikTokPixel";
import {
  loadFacebookPixel,
  sendLeadEventFb,
} from "../../Utils/FBQPurchaseEvent";
import { sendLeadEventGTM } from "../../Utils/GTMPurchaseEvent";
import { blobToFile } from "../Utils/ProcessImages";

const StepperContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(2),
}));

export default function PublicRegistration() {
  const clientFormRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const step = urlParams.get("step");
    return step ? parseInt(step) - 1 : 0; // Convert to 0-based index
  });
  const [products, setProducts] = useState<any[]>([]);
  const [orderTypes, setOrderTypes] = useState<any[]>([]);
  const [workPoints, setWorkPoints] = useState<any[]>([]);
  const [selectedWorkPoint, setSelectedWorkPoint] = useState<any | null>(null);
  const [selectedOrderType, setSelectedOrderType] = useState<any | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<string>("");
  const [clientData, setClientData] = useState<ClientModel>(new ClientModel());
  const [loading, setLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);

  // Validation schema for public registration
  const clientValidationSchema = yup.object().shape({
    firstName: yup.string().required(Vocabulary.requiredField),
    lastName: yup.string().required(Vocabulary.requiredField),
    email: yup
      .string()
      .email(Vocabulary.emailValidation)
      .required(Vocabulary.requiredField),
    phone: yup
      .string()
      .required(Vocabulary.requiredField)
      .matches(/^(\+4|4|0)\d{8,9}$/, Vocabulary.phoneValidation),
    CNP: yup.string().required(Vocabulary.requiredField),
    ciIssuedDate: yup
      .string()
      .test(
        "valid-date",
        "Invalid date format",
        (value) => !value || isValidDate(value)
      )
      .optional(),
    address: yup.object().shape({
      country: yup.string().required(Vocabulary.requiredField),
      county: yup.string().required(Vocabulary.requiredField),
      city: yup.string().required(Vocabulary.requiredField),
    }),
  });

  const steps = [
    Vocabulary.registerStep1,
    Vocabulary.registerStep2,
    Vocabulary.registerStep3,
  ];

  useEffect(() => {
    fetchOrderTypes();
    fetchWorkPoints();

    // Load TikTok Pixel
    const tiktokPixelId = process.env.REACT_APP_TIKTOK_PIXEL_ID;
    if (tiktokPixelId) {
      loadTikTokPixel(tiktokPixelId);
    }

    // Track PageView (Pixel already initialized in index.html)
    if ((window as any).fbq) {
      (window as any).fbq("track", "PageView");
    }
  }, []);

  useEffect(() => {
    if (selectedWorkPoint && selectedOrderType) {
      fetchProducts();
    }
  }, [selectedWorkPoint, selectedOrderType]);

  // Scroll to client form when step 1 is rendered (after product selection)
  useEffect(() => {
    if (activeStep === 1) {
      setTimeout(() => {
        clientFormRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200);
    }
  }, [activeStep]);

  /**
   * Fetches order types from the backend.
   * @returns {Promise<void>}
   */
  const fetchOrderTypes = async () => {
    try {
      const response = (await getData(endpoints.orderTypes)) as any;
      if (response && !response.error) {
        setOrderTypes(response.data || response || []);
      }
    } catch (error) {
      console.error("Error fetching order types:", error);
    }
  };

  /**
   * Fetches work points from the backend.
   * @returns {Promise<void>}
   */
  const fetchWorkPoints = async () => {
    try {
      const response = (await getData(endpoints.workPoints)) as any;
      if (response && !response.error) {
        setWorkPoints(response.data || response || []);
      }
    } catch (error) {
      console.error("Error fetching work points:", error);
    }
  };

  /**
   * Fetches products from the backend based on the selected work point and order type.
   * @returns {Promise<void>}
   */
  const fetchProducts = async () => {
    try {
      setLoading(true);

      const response = (await getData(
        `${endpoints.getProducts}/visible-for-online-enrollment?orderTypeId=${selectedOrderType?._id}`
      )) as any;
      if (response && !response.error) {
        setProducts(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates the current URL with the given step number, so that the registration process
   * can be bookmarked or shared.
   *
   * @param step 0-based index of the step to update the URL with.
   */
  const updateUrlWithStep = (step: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set("step", (step + 1).toString()); // Convert to 1-based step
    window.history.pushState({}, "", url.toString());
  };

  /**
   * Navigates to the specified step in the registration process.
   *
   * Updates the active step in the state, modifies the URL to reflect
   * the current step, and scrolls the window to the top.
   *
   * @param step - The 0-based index of the step to navigate to.
   */

  const goToStep = (step: any) => {
    setActiveStep(step);
    updateUrlWithStep(step);
    window.scrollTo(0, 0);
  };

  /**
   * Handles the "Next" button click by navigating to the next step in the registration process.
   *
   * If the current step is not the last step, it will navigate to the next step.
   */
  const handleNext = () => {
    if (activeStep == 1) {
      sendAnalyticsLeadEvent();
    }

    if (activeStep < steps.length - 1) {
      goToStep(activeStep + 1);
    }
  };

  /**
   * Handles the "Back" button click by navigating to the previous step in the registration process.
   *
   * If the current step is not the first step, it will navigate to the previous step.
   */

  const handleBack = () => {
    if (activeStep > 0) {
      goToStep(activeStep - 1);
    }
  };

  /**
   * Handles a change in the selected workpoint by updating the state.
   *
   * Also resets the selected order type, product and price when the workpoint changes.
   *
   * @param workPoint The newly selected workpoint (can be null for deselection).
   */
  const handleWorkPointSelect = (workPoint: WorkPointModel | null) => {
    setSelectedWorkPoint(workPoint);
    setSelectedProduct(null); // Reset product selection when workpoint changes
    setSelectedPrice(""); // Reset selected price when workpoint changes
  };

  /**
   * Handles a change in the selected order type by updating the state.
   *
   * Also resets the selected product and price when the order type changes.
   *
   * @param orderType The newly selected order type.
   */
  const handleOrderTypeSelect = (orderType: any) => {
    setSelectedOrderType(orderType);
    setSelectedProduct(null); // Reset product selection when order type changes
    setSelectedPrice(""); // Reset selected price when order type changes
  };

  /**
   * Handles a change in the selected product by updating the state.
   *
   * If a `selectedPrice` is provided, it will be used as the selected price.
   * Otherwise, the product's default price will be used.
   *
   * @param {ProductsModel} product The selected product.
   * @param {string} [selectedPrice] The price of the selected product to use.
   */
  const handleProductSelect = (
    product: ProductsModel,
    selectedPrice?: string
  ) => {
    setSelectedProduct({ ...product, price: selectedPrice });
    setSelectedPrice(selectedPrice || product.price || "");
  };

  // Get available prices for a product based on current workpoint
  const getAvailablePrices = (product: any): any[] => {
    const pricesArray = product.prices || [];

    if (pricesArray && pricesArray.length > 0 && selectedWorkPoint) {
      const workPointPrices = pricesArray.filter((p: any) => {
        // Handle MongoDB ObjectId structure: workPointId could be {$oid: "..."} or just the string
        const workPointId =
          p.workPointId?.$oid ||
          p.workPointId?._id ||
          p.workPointId ||
          p.workPoint?.$oid ||
          p.workPoint?._id ||
          p.workPoint;
        return workPointId === selectedWorkPoint._id;
      });

      return workPointPrices.length > 0 ? workPointPrices : [];
    }

    return [];
  };

  // Get the current price to display for a product
  const getCurrentPrice = (product: any): string => {
    const availablePrices = getAvailablePrices(product);
    if (availablePrices.length > 0) {
      return availablePrices[0].price || "0";
    }

    return product.price || "0";
  };

  /**
   * Handles a change in client data by updating the state.
   *
   * @param {keyof ClientModel} field - The field in the client data to update.
   * @param {any} value - The new value for the specified field.
   */
  const handleClientDataChange = (field: keyof ClientModel, value: any) => {
    setClientData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Handles the completion of an ID scan by processing and updating client data.
   *
   * @param {any} data - The data object returned from the ID scan process, expected to contain a `result` property.
   *
   * The function extracts relevant fields from the scanned data's result and updates the client data state
   * with the extracted information such as CNP, first name, last name, gender, CI details, etc.
   * If a field is missing in the scanned data, it defaults to an empty string.
   */

  const handleScanComplete = (data: any) => {
    const processedData = data.result;

    // Extract and validate the issued and expiry dates from validity field
    let ciIssuedDate = "";
    let ciExpirationDate = "";
    let file: any = null;
    if (processedData?.validity) {
      const dateParts = processedData.validity
        .split("-")
        .map((date: string) => date.trim());
      // Extract and validate issued date (first part)
      if (dateParts[0] && isValidDate(dateParts[0])) {
        ciIssuedDate = dateParts[0];
      }
      // Extract and validate expiry date (second part)
      if (dateParts[1] && isValidDate(dateParts[1])) {
        ciExpirationDate = dateParts[1];
      }
    }

    const imagePath = data?.image?.[0]?.path;

    if (imagePath && imagePath.startsWith("data:")) {
      try {
        file = blobToFile(imagePath);
      } catch (error) {
        console.error("Error converting CI image to file:", error);
      }
    }

    // Fill data from scanned ID
    setClientData((prev) => ({
      ...prev,
      CNP: processedData?.CNP || "",
      firstName: processedData?.firstName || "",
      lastName: processedData?.lastName || "",
      address: {
        country: processedData?.country || "",
        city: processedData?.birthPlace || "",
        county: processedData?.addressCounty || "",
        details: null,
        postcode: null,
      },
      // Additional fields that might be useful for future use
      gender: processedData?.sex || "",
      ciSerialNumber: processedData?.series || "",
      ciNumber: processedData?.number || "",
      ciIssuedBy: processedData?.issuingAuthority || "",
      ciIssuedDate,
      ciExpirationDate,
      ci: file,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Create client registration
      const registrationData = {
        ...clientData,
        workPointId: selectedWorkPoint?._id,
        selectedProductId: selectedProduct?._id,
        selectedPrice: selectedPrice,
        agreeTerms,
        agreePrivacy,
      };

      await postData(endpoints.client.main, registrationData);
    } catch (error) {
      console.error("Error creating registration:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sends a lead event to Google Analytics, Facebook and TikTok pixels.
   */
  const sendAnalyticsLeadEvent = () => {
    // Send Lead event to Analytics pixels
    sendLeadEventGTM(clientData, selectedProduct, selectedPrice);

    // send Lead Event event for facebook pixel
    if (process.env.REACT_APP_FACEBOOK_PIXEL_ID) {
      loadFacebookPixel(process.env.REACT_APP_FACEBOOK_PIXEL_ID);
      setTimeout(() => {
        sendLeadEventFb(clientData, selectedProduct, selectedPrice);
      }, 1000);
    }

    // send LEAD EVENT for tiktok pixel
    loadTikTokPixel(process.env.REACT_APP_TIKTOK_PIXEL_ID || "");
    setTimeout(() => {
      sendLeadEventTikTok(clientData, selectedProduct, selectedPrice);
    }, 1000);
  };

  const renderWorkPointAndProductSelection = () => (
    <div className={styles.maxWidthContainer}>
      <div className={styles.stepsContainer}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <svg
                className={styles.icon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <div>
              <h1 className={styles.headerTitle}>Înregistrare cursant</h1>
            </div>
          </div>
        </div>

        {/* City Selection (Work Points) */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <svg
                className={styles.iconSmall}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>Selectează orașul</h3>
          </div>
          <div className={`${styles.gridContainer} ${styles.gridThreeColumns}`}>
            {workPoints.map((workPoint: WorkPointModel) => (
              <button
                key={workPoint._id}
                onClick={() => handleWorkPointSelect(workPoint)}
                className={`${styles.button} ${
                  selectedWorkPoint?._id === workPoint._id
                    ? styles.buttonSelected
                    : ""
                }`}
              >
                <div>{workPoint.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Parallel Layout: Service Type and Products */}
        <div className={styles.parallelContainer}>
          {/* Service Type Selection (Order Types) - Left Side */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <svg
                  className={styles.iconSmall}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className={styles.cardTitle}>Tipul de serviciu</h3>
            </div>
            <div className={styles.gridContainer}>
              {orderTypes.map((orderType: any) => (
                <div
                  key={orderType._id}
                  className={`${styles.radioContainer} ${
                    selectedOrderType?._id === orderType._id
                      ? styles.radioContainerSelected
                      : ""
                  }`}
                  onClick={() => handleOrderTypeSelect(orderType)}
                >
                  <div className={styles.radioContent}>
                    <div
                      className={`${styles.radioButton} ${
                        selectedOrderType?._id === orderType._id
                          ? styles.radioButtonSelected
                          : ""
                      }`}
                    ></div>
                    <div className={styles.radioLabel}>
                      {Vocabulary[orderType.name]}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Products Selection - Right Side */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <svg
                  className={styles.iconSmall}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <h3 className={styles.cardTitle}>Cursuri disponibile</h3>
            </div>
            {!selectedWorkPoint ? (
              <div className={styles.emptyState}>
                <h4 className={styles.emptyStateTitle}>Selectează orașul</h4>
                <p className={styles.emptyStateText}>
                  Alege mai întâi orașul pentru a vedea cursurile disponibile
                </p>
              </div>
            ) : !selectedOrderType ? (
              <div className={styles.emptyState}>
                <h4 className={styles.emptyStateTitle}>
                  Selectează tipul de serviciu
                </h4>
                <p className={styles.emptyStateText}>
                  Alege mai întâi tipul de serviciu pentru a vedea cursurile
                  disponibile
                </p>
              </div>
            ) : loading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p className={styles.loadingText}>Se încarcă cursurile...</p>
              </div>
            ) : products.length === 0 ? (
              <div className={styles.emptyState}>
                <h4 className={styles.emptyStateTitle}>
                  Nu sunt cursuri disponibile
                </h4>
                <p className={styles.emptyStateText}>
                  Nu există cursuri disponibile pentru această combinație de
                  locație și tip de serviciu
                </p>
              </div>
            ) : (
              <div className={styles.productList}>
                {products.map((product: ProductsModel) => (
                  <div
                    key={product._id}
                    className={`${styles.productItem} ${
                      selectedProduct?._id === product._id
                        ? styles.productItemSelected
                        : ""
                    }`}
                    onClick={() =>
                      handleProductSelect(product, getCurrentPrice(product))
                    }
                  >
                    <div className={styles.productContent}>
                      <div className={styles.productLeft}>
                        {selectedProduct?._id === product._id && (
                          <svg
                            className={styles.checkIcon}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        )}
                        <div>
                          <h4 className={styles.productTitle}>
                            {product.name}
                          </h4>
                          {(product as any).description && (
                            <p className={styles.productDescription}>
                              {(product as any).description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div
                        className={`${styles.productPrice} ${
                          selectedProduct?._id === product._id
                            ? styles.productPriceSelected
                            : ""
                        }`}
                      >
                        {getCurrentPrice(product)} RON
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Benefits Section */}
        <BenefitsSection isCertificate={selectedOrderType?.name == "Atestat"} />
      </div>
    </div>
  );

  const renderClientForm = () => (
    <div ref={clientFormRef}>
      <ClientFormStep
        clientData={clientData}
        agreeTerms={agreeTerms}
        agreePrivacy={agreePrivacy}
        onClientDataChange={handleClientDataChange}
        onAgreeTermsChange={setAgreeTerms}
        onAgreePrivacyChange={setAgreePrivacy}
        onScanComplete={handleScanComplete}
      />
    </div>
  );

  /**
   * This function renders the third step of the public registration process, which is the payment step.
   * It shows a payment summary before redirecting to the payment gateway.
   *
   * @returns The JSX for the third step of the public registration process.
   */
  const renderPaymentSummary = () => {
    return (
      <div className={styles.maxWidthContainer}>
        <div className={styles.stepsContainer}>
          {/* Payment Redirect Card */}
          <div className={styles.card}>
            <div
              style={{
                display: "flex",
                flexDirection: "column" as const,
                alignItems: "center",
                justifyContent: "center",
                padding: "3rem 2rem",
                textAlign: "center" as const,
              }}
            >
              {/* Payment Icon */}
              <div
                style={{
                  width: "4rem",
                  height: "4rem",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <svg
                  style={{
                    width: "2rem",
                    height: "2rem",
                    color: "currentColor",
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              </div>

              {/* Title */}
              <h2
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "#1f2937",
                  marginBottom: "1rem",
                }}
              >
                {Vocabulary.paymentRedirect}
              </h2>

              {/* Message */}
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "1rem",
                  lineHeight: "1.5",
                  marginBottom: "2rem",
                  maxWidth: "28rem",
                }}
              >
                {Vocabulary.paymentRedirectMessage}
              </p>

              {/* Loading indicator */}
              <div
                style={{
                  width: "2rem",
                  height: "2rem",
                  border: "3px solid #e5e7eb",
                  borderTop: "3px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Redirect to payment when step 2 is reached
  useEffect(() => {
    const handlePaymentStep = async () => {
      if (activeStep === 2) {
        const orderCreated = await createaOrder();

        // Only proceed to payment if order was created successfully
        if (!orderCreated) {
          // Go back to previous step if order creation failed
          goToStep(1);
          return;
        }

        if (selectedPrice) {
          const amount = await getOrderAdvanceAmount(Number(selectedPrice));
          generatePaymentUrl({
            clientId: localStorage.getItem("processingClientId") || undefined,
            clientData,
            amount,
            orderDescription: `${Vocabulary.advance} ${selectedProduct?.name} - ${selectedWorkPoint?.name}`,
            saveOrderId: true,
            orderId: localStorage.getItem("processingOrderId") || undefined,
            purpose: PAYMENT_TYPES.SCHOOL_ENROLLING,
          });
        }
      }
    };

    handlePaymentStep();
  }, [activeStep]);

  /**
   * Create a new order and sends a POST request to the API.
   * @param {Object} data - The data to be sent in the request body.
   * @param {Object} data.client_id - The client data.
   * @param {Object} data.product - The selected product.
   * @param {string} data.statusSlug - The status slug of the order.
   * @returns {Promise<boolean>} - Returns true if order was created successfully, false otherwise.
   */
  const createaOrder = async (): Promise<boolean> => {
    const data = {
      client_id: {
        ...clientData,
        workPointId: JSON.stringify(selectedWorkPoint),
      },
      product: selectedProduct,
      statusSlug: EContactStatus.AVANS_NEPLATIT_SLUG,
      source: OrderSource.ONLINE,
    };

    const ciFile = data?.client_id?.ci;
    if (ciFile instanceof File) {
      const formData = new FormData();
      appendFormData(formData, data);

      // Append the CI file
      formData.append("ci", ciFile);

      return saveOrder(formData);
    } else {
      return saveOrder(data);
    }
  };

  /**
   * Saves a new order to the backend.
   * @param {FormData | OrderModel} data - The data to be sent in the request body.
   * @returns {Promise<boolean>} - Returns true if order was saved successfully, false otherwise.
   */
  const saveOrder = async (data: FormData | OrderPayload): Promise<boolean> => {
    try {
      const res = await postData(endpoints.ordersEndpoint, data);
      if (res?.data) {
        const newOrderId = res.data._id;
        localStorage.setItem("processingOrderId", newOrderId);
        localStorage.setItem(
          "processingClientId",
          res.data.client_id?._id || ""
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error during API request:", error);
      return false;
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return (
          selectedWorkPoint !== null &&
          selectedOrderType !== null &&
          selectedProduct !== null
        );
      case 1:
        try {
          clientValidationSchema.validateSync(clientData);
          return agreeTerms && agreePrivacy;
        } catch (error) {
          return false;
        }
      case 2:
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderWorkPointAndProductSelection();
      case 1:
        return renderClientForm();
      case 2:
        return renderPaymentSummary();

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        paddingTop: "1rem",
        paddingBottom: "1rem",
      }}
    >
      <Container maxWidth="xl">
        <>
          <StepperContainer elevation={2}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </StepperContainer>

          {renderStepContent()}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={6}
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{ visibility: activeStep === 0 ? "hidden" : "visible" }}
            >
              {Vocabulary.back}
            </Button>

            <Button
              variant="contained"
              size="large"
              endIcon={
                activeStep === steps.length - 1 ? undefined : <ArrowForward />
              }
              onClick={
                activeStep === steps.length - 1 ? handleSubmit : handleNext
              }
              disabled={!canProceed() || loading}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              {Vocabulary.continue}
            </Button>
          </Box>
        </>
      </Container>
    </div>
  );
}
